import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Model, ModalHeader, ModalBody, LogoSVG } from 'components/atoms';
import { NewBreadCrumb, TaskDetailsModal } from 'components/molecules';
import { ViewScreen } from 'components/template';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { getEmployeeGoals, updateSubtask, acceptGoalAssignment, createSubtasks, updateGoalStatus, deleteSubtask } from 'store/Goals';
import { getGoalReview } from 'store/Admin';
import { HRReviewEmployeeGoalAssignment, HRReviewGoal, HRReviewSubtask } from 'models/models';
import { UpdateSubtaskRequest, CreateSubtasksRequest } from 'services/Goals';
import { useCurrentEmployeeId } from 'services/hooks';
import moment from 'moment';
import Swal from 'sweetalert2';
import './index.scss';


const ICONS = {
  TASKS: '/icons/list-check.svg',
  COMPLETED: '/icons/list-check.svg',
  IN_PROGRESS: '/icons/clock.svg',
};

interface Review {
  id: string;
  comment: string;
  rating: number;
  reviewedAt: string;
  reviewedBy: string;
  goalId?: string;
}

interface SubTask {
  id: string;
  title: string;
  description: string;
  deadline: string;
  status: 'Completed' | 'Pending' | 'In Progress';
  priority: 'High' | 'Medium' | 'Low';
}

interface SubTaskWithQuarter extends SubTask {
  quarter?: 'Q1' | 'Q2' | 'Q3' | 'Q4';
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'Completed' | 'Pending' | 'In Progress';
  reviews: Review[];
  subtasks: SubTaskWithQuarter[];
  quarter?: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  isParentTask?: boolean;
  parentTaskId?: string;
  combinedGoalId?: string;
  deadline: string;
  assignedDate: string;
  goalAssignmentGoalId: string;
}

interface Supervisor {
  id: string;
  name: string;
  email: string;
  designation: string;
}

const GoalDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  goal: Task | null;
  onAccept: (goalId: string) => void;
  onDecline: (goalId: string) => void;
  isProcessing: boolean;
}> = ({ isOpen, onClose, goal, onAccept, onDecline, isProcessing }) => {
  if (!goal) return null;

  const handleAccept = () => {
    onAccept(goal.id);
  };

  const handleDecline = () => {
    onDecline(goal.id);
  };

  return (
    <Model showModal={isOpen} onClose={onClose} modalSize="lg">
      <ModalHeader headerTitle="Goal Details" onClose={onClose} />
      <ModalBody>
        <div className="goal-details-modal">
          <div className="goal-info-section">
            <div className="goal-title-section">
              <Typography type="heading4" text={goal.title} className="goal-title" />
              <div className="goal-meta">
                <Typography
                  type="small_regular"
                  text={`Assigned: ${new Date(goal.assignedDate).toLocaleDateString()}`}
                  className="meta-text"
                />
                <Typography
                  type="small_regular"
                  text={`Deadline: ${new Date(goal.deadline).toLocaleDateString()}`}
                  className="meta-text"
                />
              </div>
            </div>

            <div className="goal-description-section">
              <Typography type="base_medium" text="Description:" className="section-label" />
              <div className="description-content">
                <Typography type="base_regular" text={goal.description} className="description-text" />
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <div className="action-buttons">
              <Button
                type="secondary"
                size="base"
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type="secondary"
                size="base"
                onClick={handleDecline}
                disabled={isProcessing}
                className="decline-btn"
              >
                {isProcessing ? 'Processing...' : 'Decline Goal'}
              </Button>
              <Button
                type="primary"
                size="base"
                onClick={handleAccept}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Accept Goal'}
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Model>
  );
};


const SubtaskDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  subtask: SubTaskWithQuarter | null;
  goalTitle: string;
  taskId: string;
  isSaved: boolean;
  onStatusChange: (taskId: string, subtaskId: string, newStatus: Task['status']) => void;
  onDeadlineChange: (taskId: string, subtaskId: string, newDeadline: string) => void;
  onSubtaskUpdate: (taskId: string, subtaskId: string, updates: Partial<SubTask>) => void;
  onSubtaskDelete?: (taskId: string, subtaskId: string) => void;
  quarterRange: { start: string; end: string } | null;
}> = ({ isOpen, onClose, subtask, goalTitle, taskId, isSaved, onStatusChange, onDeadlineChange, onSubtaskUpdate, onSubtaskDelete, quarterRange }) => {
  if (!subtask) return null;

  const handleFieldChange = (field: keyof SubTask, value: string) => {
    if (field === 'deadline') {
      onDeadlineChange(taskId, subtask.id, value);
    } else {
      onSubtaskUpdate(taskId, subtask.id, { [field]: value });
    }
  };

  return (
    <Model showModal={isOpen} onClose={onClose} modalSize="lg">
      <ModalHeader headerTitle={`Subtask Details ${!isSaved ? '(Edit Mode)' : ''}`} onClose={onClose} />
      <ModalBody>
        <div className="subtask-details-modal">
          <div className="modal-section">
            <div className="goal-info">
              <Typography type="small_medium" text="Goal:" className="label" />
              <Typography type="base_medium" text={goalTitle} className="goal-name" />
            </div>
          </div>

          <div className={`modal-section ${!isSaved ? 'edit-mode' : ''}`}>
            <Typography type="heading5" text="Subtask Information" />
            <div className="info-grid">
              <div className="info-item">
                <Typography type="small_medium" text="Title:" className="label" />
                {!isSaved ? (
                  <input
                    type="text"
                    value={subtask.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    className="text-input"
                    placeholder="Enter subtask title"
                    required
                  />
                ) : (
                  <div className="title-display">
                    <Typography type="base_regular" text={subtask.title} />
                  </div>
                )}
              </div>
            </div>
            <div className="info-grid-2">
              <div className="info-item">
                <Typography type="small_medium" text="Priority:" className="label" />
                {!isSaved ? (
                  <select
                    value={subtask.priority}
                    onChange={(e) => handleFieldChange('priority', e.target.value)}
                    className="priority-dropdown"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                ) : (
                  <span className={`priority-tag priority-${subtask.priority.toLowerCase()}`}>
                    {subtask.priority}
                  </span>
                )}
              </div>
              <div className="info-item">
                <Typography type="small_medium" text="Status:" className="label" />
                  <select
                    value={subtask.status}
                    onChange={(e) => onStatusChange(taskId, subtask.id, e.target.value as Task['status'])}
                    className={`status-dropdown status-${subtask.status.toLowerCase().replace(' ', '-')}`}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
              </div>
            </div>
          </div>

          <div className="modal-section">
            <Typography type="heading5" text="Description" />
            <div className="description-content">
              {!isSaved ? (
                <textarea
                  value={subtask.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  className="description-textarea"
                  placeholder="Enter subtask description (optional)"
                  rows={6}
                />
              ) : (
                <div className="description-display">
                  <Typography type="base_regular" text={subtask.description || 'No description provided'} />
                </div>
              )}
            </div>
          </div>

          <div className="modal-section">
            <Typography type="heading5" text="Deadline" />
            <div className="deadline-content">
              {!isSaved ? (
                <div className="deadline-input">
                  <input
                    type="date"
                    value={subtask.deadline ? new Date(subtask.deadline).toISOString().split('T')[0] : ''}
                    onChange={(e) => onDeadlineChange(taskId, subtask.id, e.target.value)}
                    min={quarterRange?.start}
                    max={quarterRange?.end}
                    className="date-input"
                  />
                  {quarterRange && (
                    <Typography
                      type="small_regular"
                      text={`Must be between ${new Date(quarterRange.start).toLocaleDateString()} and ${new Date(quarterRange.end).toLocaleDateString()}`}
                      className="date-range-info"
                    />
                  )}
                </div>
              ) : (
                <Typography type="base_regular" text={new Date(subtask.deadline).toLocaleDateString()} />
              )}
            </div>
          </div>

          {subtask.quarter && (
            <div className="modal-section">
              <Typography type="heading5" text="Quarter Assignment" />
              <Typography type="base_regular" text={subtask.quarter} />
            </div>
          )}

          {!isSaved && onSubtaskDelete && (
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  onSubtaskDelete(taskId, subtask.id);
                }}
                className="delete-subtask-btn"
                title="Delete subtask"
              >
                <img src="/icons/delete.svg" alt="Delete" />
              </button>
            </div>
          )}
        </div>
      </ModalBody>
    </Model>
  );
};

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const AVAILABLE_TASKS_ID = 'available-tasks';

const getQuarterDateRanges = (goalAssignedDate: string, goalDeadline: string) => {
  const startDate = new Date(goalAssignedDate);
  const endDate = new Date(goalDeadline);

  const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  
  const daysPerQuarter = Math.floor(totalDays / 4);
  const remainingDays = totalDays % 4;

  const ranges: { [key: string]: { start: string; end: string } } = {};
  let currentDate = new Date(startDate);

  quarters.forEach((quarter, index) => {
    const quarterStart = new Date(currentDate);
    
    const quarterLength = daysPerQuarter + (index < remainingDays ? 1 : 0);
    
    const quarterEnd = new Date(quarterStart);
    quarterEnd.setDate(quarterStart.getDate() + quarterLength - 1);

    if (index === 3) {
      quarterEnd.setTime(endDate.getTime());
    }

    ranges[quarter] = {
      start: quarterStart.toISOString().split('T')[0],
      end: quarterEnd.toISOString().split('T')[0]
    };

    currentDate = new Date(quarterEnd);
    currentDate.setDate(quarterEnd.getDate() + 1);
  });

  return ranges;
};

const defaultQuarterDateRanges = {
  Q1: { start: '2024-01-01', end: '2024-03-31' },
  Q2: { start: '2024-04-01', end: '2024-06-30' },
  Q3: { start: '2024-07-01', end: '2024-09-30' },
  Q4: { start: '2024-10-01', end: '2024-12-31' }
};

export const MyGoals = () => {
  const dispatch = useAppDispatch();
  const { goalAssignments, error, loading } = useAppSelector((state: RootState) => state.goals);
  const { goalReviews } = useAppSelector((state: RootState) => state.admin);
  const { employeeId, isLoading: employeeIdLoading } = useCurrentEmployeeId();

  const transformApiDataToTasks = (apiData: HRReviewEmployeeGoalAssignment[]): { [id: string]: Task & { acceptanceStatus?: string | null } } => {
    if (!apiData || apiData.length === 0) return {};

    const assignment = apiData[0];
    const tasks: { [id: string]: Task & { acceptanceStatus?: string | null } } = {};

    assignment.goals.forEach((goal: HRReviewGoal) => {
      const transformedSubtasks: SubTaskWithQuarter[] = goal.subtasks
        ? goal.subtasks.map((subtask: HRReviewSubtask) => ({
          id: subtask.id,
          title: subtask.title,
          description: subtask.description,
          deadline: subtask.deadline,
          status: subtask.status,
          priority: subtask.priority === 'HIGH' ? 'High' : subtask.priority === 'MEDIUM' ? 'Medium' : 'Low',
          quarter: subtask.quarter as 'Q1' | 'Q2' | 'Q3' | 'Q4'
        }))
        : [];

      tasks[goal.id] = {
        id: goal.id,
        title: goal.title,
        description: goal.description,
        status: goal.status,
        reviews: [],
        subtasks: transformedSubtasks,
        deadline: goal.endDate,
        assignedDate: assignment.publishedAt.split('T')[0],
        goalAssignmentGoalId: goal.id,
        acceptanceStatus: goal.acceptanceStatus || null
      };
    });

    return tasks;
  };

  const fetchGoalReviews = useCallback(async (tasksData: { [id: string]: Task }) => {
    const goalIds = Object.values(tasksData).map(task => task.goalAssignmentGoalId);

    goalIds.forEach(goalId => {
      dispatch(getGoalReview(goalId));
    });
  }, [dispatch]);

  const [tasks, setTasks] = useState<{ [id: string]: Task & { acceptanceStatus?: string | null } }>({});
  const [draggedSubtask, setDraggedSubtask] = useState<{ taskId: string; subtaskId: string } | null>(null);
  const [draggedOver, setDraggedOver] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState<{ subtask: SubTaskWithQuarter; taskId: string } | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [selectedGoalForAcceptance, setSelectedGoalForAcceptance] = useState<string | null>(null);
  const [isGoalDetailsModalOpen, setIsGoalDetailsModalOpen] = useState(false);
  const [isProcessingGoal, setIsProcessingGoal] = useState(false);
  const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasNewGoals, setHasNewGoals] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);

  useEffect(() => {
    console.log('Current employee ID:', employeeId);
    if (employeeId) {
      dispatch(getEmployeeGoals(employeeId));
    }
  }, [dispatch, employeeId]);

  useEffect(() => {
    if (goalAssignments && goalAssignments.length > 0) {
      const transformedTasks = transformApiDataToTasks(goalAssignments);
      
      setTasks(prevTasks => {
        if (!isGlobalEditMode || Object.keys(prevTasks).length === 0) {
          return transformedTasks;
        } else {
          const updatedTasks = { ...prevTasks };
          Object.keys(transformedTasks).forEach(taskId => {
            if (updatedTasks[taskId]) {
              updatedTasks[taskId] = {
                ...updatedTasks[taskId],
                status: transformedTasks[taskId].status,
                acceptanceStatus: transformedTasks[taskId].acceptanceStatus
              };
              
              updatedTasks[taskId].subtasks = updatedTasks[taskId].subtasks.map(localSubtask => {
                const serverSubtask = transformedTasks[taskId].subtasks.find(s => s.id === localSubtask.id);
                if (serverSubtask && !localSubtask.id.startsWith('subtask-')) {
                  return {
                    ...localSubtask,
                    status: serverSubtask.status
                  };
                }
                return localSubtask;
              });
              
              const localSubtaskIds = new Set(updatedTasks[taskId].subtasks.map(s => s.id));
              const newServerSubtasks = transformedTasks[taskId].subtasks.filter(s => !localSubtaskIds.has(s.id));
              updatedTasks[taskId].subtasks.push(...newServerSubtasks);
            } else {
              updatedTasks[taskId] = transformedTasks[taskId];
            }
          });
          return updatedTasks;
        }
      });

      setSelectedSubtask(prevSelected => {
        if (prevSelected && transformedTasks[prevSelected.taskId]) {
          const updatedSubtask = transformedTasks[prevSelected.taskId].subtasks.find(
            s => s.id === prevSelected.subtask.id
          );
          if (updatedSubtask) {
            return {
              ...prevSelected,
              subtask: updatedSubtask
            };
          }
        }
        return prevSelected;
      });

      const hasNewGoalsCheck = Object.values(transformedTasks).some(task =>
        task.subtasks.length === 0 || task.subtasks.every(subtask => subtask.id.startsWith('subtask-'))
      );
      setHasNewGoals(hasNewGoalsCheck);

      if (hasNewGoalsCheck) {
        setIsGlobalEditMode(true);
      }

      fetchGoalReviews(transformedTasks);
    }
  }, [goalAssignments, fetchGoalReviews, isGlobalEditMode]);

  const supervisors: Supervisor[] = goalAssignments && goalAssignments.length > 0 && goalAssignments[0].supervisors
    ? goalAssignments[0].supervisors
    : [];

  const getQuarterDateRangesForTasks = () => {
    const firstTask = Object.values(tasks)[0];
    if (firstTask && firstTask.assignedDate && firstTask.deadline) {
      return getQuarterDateRanges(firstTask.assignedDate, firstTask.deadline);
    }
    return defaultQuarterDateRanges;
  };

  const quarterDateRanges = getQuarterDateRangesForTasks();

  const handleDragStart = (e: React.DragEvent, taskId: string, subtaskId: string) => {
    setDraggedSubtask({ taskId, subtaskId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDraggedOver(columnId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDraggedOver(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetQuarter: string) => {
    e.preventDefault();
    if (!draggedSubtask) return;

    const { taskId, subtaskId } = draggedSubtask;
    const task = tasks[taskId];

    if (task.acceptanceStatus !== 'Accepted') {
      await Swal.fire({
        title: 'Cannot Move Subtask',
        text: 'You must accept the goal before organizing subtasks.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      setDraggedSubtask(null);
      setDraggedOver(null);
      return;
    }

    const subtask = task.subtasks.find(s => s.id === subtaskId);
    if (!subtask) return;

    const newQuarter = targetQuarter === AVAILABLE_TASKS_ID ? undefined : targetQuarter as 'Q1' | 'Q2' | 'Q3' | 'Q4';
    const newDeadline = targetQuarter !== AVAILABLE_TASKS_ID
      ? quarterDateRanges[targetQuarter as keyof typeof quarterDateRanges].end
      : subtask.deadline;

    setTasks(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        subtasks: prev[taskId].subtasks.map(subtask =>
          subtask.id === subtaskId
            ? {
              ...subtask,
              quarter: newQuarter,
              deadline: newDeadline
            }
            : subtask
        )
      }
    }));

    setPendingChanges(true);

    setDraggedSubtask(null);
    setDraggedOver(null);
  };

  const handleDragEnd = () => {
    setDraggedSubtask(null);
    setDraggedOver(null);
  };

  const handleStatusChange = async (taskId: string, newStatus: Task['status']) => {
    const task = tasks[taskId];
    if (!task) return;

    const originalStatus = task.status;

    try {
      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          status: newStatus
        }
      }));

      await dispatch(updateGoalStatus({
        goalAssignmentGoalId: task.goalAssignmentGoalId,
        status: newStatus
      }));

    } catch (error) {
      console.error('Error updating goal status:', error);

      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          status: originalStatus
        }
      }));

      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update goal status. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSubtaskStatusChange = async (taskId: string, subtaskId: string, newStatus: Task['status']) => {
    if (subtaskId.startsWith('subtask-')) {
      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          subtasks: prev[taskId].subtasks.map(subtask =>
            subtask.id === subtaskId ? { ...subtask, status: newStatus } : subtask
          )
        }
      }));
      
      if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
        setSelectedSubtask({
          ...selectedSubtask,
          subtask: {
            ...selectedSubtask.subtask,
            status: newStatus
          }
        });
      }
      
      setPendingChanges(true);
      return;
    }

    const task = tasks[taskId];
    const originalSubtask = task.subtasks.find(s => s.id === subtaskId);
    if (!originalSubtask) return;

    try {
      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          subtasks: prev[taskId].subtasks.map(subtask =>
            subtask.id === subtaskId ? { ...subtask, status: newStatus } : subtask
          )
        }
      }));

      if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
        setSelectedSubtask({
          ...selectedSubtask,
          subtask: {
            ...selectedSubtask.subtask,
            status: newStatus
          }
        });
      }

      const updateData: UpdateSubtaskRequest = {
        title: originalSubtask.title,
        description: originalSubtask.description,
        status: newStatus,
        priority: originalSubtask.priority.toUpperCase(),
        quarter: originalSubtask.quarter || 'Q1',
        deadline: originalSubtask.deadline
      };

      await dispatch(updateSubtask({ subtaskId, data: updateData }));

      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }

    } catch (error) {
      console.error('Error updating subtask status:', error);

      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }

      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update subtask status. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSubtaskDeadlineChange = async (taskId: string, subtaskId: string, newDeadline: string) => {
    if (subtaskId.startsWith('subtask-')) {
      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              deadline: newDeadline
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };
          }
        }

        return updatedTasks;
      });

      setPendingChanges(true);
      return;
    }

    const task = tasks[taskId];
    const originalSubtask = task.subtasks.find(s => s.id === subtaskId);
    if (!originalSubtask) return;

    const originalDeadline = originalSubtask.deadline;

    try {
      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              deadline: newDeadline
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };

            if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
              setSelectedSubtask({
                subtask: updatedSubtasks[subtaskIndex],
                taskId
              });
            }
          }
        }

        return updatedTasks;
      });

      const updateData: UpdateSubtaskRequest = {
        title: originalSubtask.title,
        description: originalSubtask.description,
        status: originalSubtask.status,
        priority: originalSubtask.priority.toUpperCase(),
        quarter: originalSubtask.quarter || 'Q1',
        deadline: newDeadline
      };

      await dispatch(updateSubtask({ subtaskId, data: updateData }));

    } catch (error) {
      console.error('Error updating subtask deadline:', error);

      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              deadline: originalDeadline
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };

            if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
              setSelectedSubtask({
                subtask: updatedSubtasks[subtaskIndex],
                taskId
              });
            }
          }
        }

        return updatedTasks;
      });

      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update subtask deadline. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSubtaskClick = (taskId: string, subtask: SubTaskWithQuarter) => {
    setSelectedSubtask({ subtask, taskId });
    setIsSubtaskModalOpen(true);
  };

  const handleSubtaskUpdate = async (taskId: string, subtaskId: string, updates: Partial<SubTask>) => {
    if (subtaskId.startsWith('subtask-')) {
      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              ...updates
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };

            if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
              setSelectedSubtask({
                subtask: updatedSubtasks[subtaskIndex],
                taskId
              });
            }
          }
        }

        return updatedTasks;
      });

      setPendingChanges(true);
      return;
    }

    const task = tasks[taskId];
    const originalSubtask = task.subtasks.find(s => s.id === subtaskId);
    if (!originalSubtask) return;

    const originalValues = {
      title: originalSubtask.title,
      description: originalSubtask.description,
      deadline: originalSubtask.deadline,
      priority: originalSubtask.priority,
      status: originalSubtask.status
    };

    try {
      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              ...updates
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };

            if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
              setSelectedSubtask({
                subtask: updatedSubtasks[subtaskIndex],
                taskId
              });
            }
          }
        }

        return updatedTasks;
      });

      const updatedSubtask = { ...originalSubtask, ...updates };
      const updateData: UpdateSubtaskRequest = {
        title: updatedSubtask.title,
        description: updatedSubtask.description,
        status: updatedSubtask.status,
        priority: updatedSubtask.priority.toUpperCase(),
        quarter: updatedSubtask.quarter || 'Q1',
        deadline: updatedSubtask.deadline
      };

      await dispatch(updateSubtask({ subtaskId, data: updateData }));

      await Swal.fire({
        title: 'Subtask Updated!',
        text: 'Subtask has been updated successfully',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (error) {
      console.error('Error updating subtask:', error);

      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
          if (subtaskIndex !== -1) {
            const updatedSubtasks = [...task.subtasks];
            updatedSubtasks[subtaskIndex] = {
              ...updatedSubtasks[subtaskIndex],
              ...originalValues
            };

            updatedTasks[taskId] = {
              ...task,
              subtasks: updatedSubtasks
            };

            if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
              setSelectedSubtask({
                subtask: updatedSubtasks[subtaskIndex],
                taskId
              });
            }
          }
        }

        return updatedTasks;
      });

      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update subtask. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSubtaskDelete = async (taskId: string, subtaskId: string) => {
    const wasSubtaskModalOpen = isSubtaskModalOpen;
    const wasTaskModalOpen = isModalOpen;
    const currentSelectedSubtask = selectedSubtask;
    const currentSelectedTask = selectedTask;

    if (selectedSubtask?.subtask.id === subtaskId && selectedSubtask?.taskId === taskId) {
      setSelectedSubtask(null);
      setIsSubtaskModalOpen(false);
    }

    if (isModalOpen && selectedTask === taskId) {
      setIsModalOpen(false);
      setSelectedTask(null);
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    if (subtaskId.startsWith('subtask-')) {
      const result = await Swal.fire({
        title: 'Delete Subtask?',
        text: 'Are you sure you want to delete this subtask?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (!result.isConfirmed) {
        if (wasSubtaskModalOpen && currentSelectedSubtask) {
          setSelectedSubtask(currentSelectedSubtask);
          setIsSubtaskModalOpen(true);
        }
        if (wasTaskModalOpen && currentSelectedTask) {
          setSelectedTask(currentSelectedTask);
          setIsModalOpen(true);
        }
        return;
      }

      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId);
          updatedTasks[taskId] = {
            ...task,
            subtasks: updatedSubtasks
          };
        }

        return updatedTasks;
      });

      setPendingChanges(true);
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Subtask?',
      text: 'Are you sure you want to delete this subtask? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    });

    if (!result.isConfirmed) {
      if (wasSubtaskModalOpen && currentSelectedSubtask) {
        setSelectedSubtask(currentSelectedSubtask);
        setIsSubtaskModalOpen(true);
      }
      if (wasTaskModalOpen && currentSelectedTask) {
        setSelectedTask(currentSelectedTask);
        setIsModalOpen(true);
      }
      return;
    }

    try {
      setTasks(prevTasks => {
        const updatedTasks = { ...prevTasks };
        const task = updatedTasks[taskId];

        if (task) {
          const updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId);
          updatedTasks[taskId] = {
            ...task,
            subtasks: updatedSubtasks
          };
        }

        return updatedTasks;
      });

      await dispatch(deleteSubtask(subtaskId));

      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }

    } catch (error) {
      console.error('Error deleting subtask:', error);

      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }

      await Swal.fire({
        title: 'Error!',
        text: 'Failed to delete subtask. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleSubtaskEdit = (taskId: string, subtaskId: string) => {
    const subtask = tasks[taskId].subtasks.find(s => s.id === subtaskId);
    if (!subtask) return; // Prevents undefined assignment
    setSelectedSubtask({ subtask, taskId });
    setIsSubtaskModalOpen(true);
  };

  const handleAcceptGoal = async (goalAssignmentGoalId: string) => {
    setSelectedGoalForAcceptance(goalAssignmentGoalId);
    setIsGoalDetailsModalOpen(true);
  };

  const handleGoalAccept = async (goalAssignmentGoalId: string) => {
    setIsProcessingGoal(true);
    try {
      await dispatch(acceptGoalAssignment({ goalAssignmentGoalId, status: 'Accepted' }));
      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }
      setIsGoalDetailsModalOpen(false);
      setSelectedGoalForAcceptance(null);
    } catch (error) {
      console.error('Error accepting goal:', error);
    } finally {
      setIsProcessingGoal(false);
    }
  };

  const handleGoalDecline = async (goalAssignmentGoalId: string) => {
    setIsProcessingGoal(true);
    try {
      await dispatch(acceptGoalAssignment({ goalAssignmentGoalId, status: 'Declined' }));
      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }
      setIsGoalDetailsModalOpen(false);
      setSelectedGoalForAcceptance(null);
    } catch (error) {
      console.error('Error declining goal:', error);
    } finally {
      setIsProcessingGoal(false);
    }
  };

  const handleGlobalSave = async () => {
    if (!pendingChanges) return;

    setIsSaving(true);

    try {
      const savePromises = Object.keys(tasks).map(async (goalId) => {
        const task = tasks[goalId];
        const newSubtasks = task.subtasks.filter(subtask => subtask.id.startsWith('subtask-'));
        const existingSubtasks = task.subtasks.filter(subtask => !subtask.id.startsWith('subtask-'));

        if (newSubtasks.length > 0) {
          const subtasksData: CreateSubtasksRequest = {
            subtasks: newSubtasks.map(subtask => ({
              title: subtask.title,
              description: subtask.description,
              deadline: subtask.deadline,
              priority: subtask.priority.toUpperCase(),
              quarter: subtask.quarter || 'Q1'
            }))
          };

          await dispatch(createSubtasks({ goalAssignmentGoalId: goalId, data: subtasksData }));
        }

        const updatePromises = existingSubtasks.map(async (subtask) => {
          const updateData: UpdateSubtaskRequest = {
            title: subtask.title,
            description: subtask.description,
            status: subtask.status,
            priority: subtask.priority.toUpperCase(),
            quarter: subtask.quarter || 'Q1',
            deadline: subtask.deadline
          };

          return dispatch(updateSubtask({ subtaskId: subtask.id, data: updateData }));
        });

        await Promise.all(updatePromises);
      });

      await Promise.all(savePromises);

      if (employeeId) {
        dispatch(getEmployeeGoals(employeeId));
      }

      setPendingChanges(false);
      setIsGlobalEditMode(false);
      setHasNewGoals(false);

      await Swal.fire({
        title: 'Success!',
        text: 'All changes saved successfully.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to save changes. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnterEditMode = () => {
    setIsGlobalEditMode(true);
    setHasNewGoals(false);
  };

  const handleCancelEdit = () => {
    if (pendingChanges) {
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Are you sure you want to cancel?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel',
        cancelButtonText: 'Continue editing'
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload data to discard changes
          if (employeeId) {
            dispatch(getEmployeeGoals(employeeId));
          }
          setIsGlobalEditMode(false);
          setPendingChanges(false);
          setHasNewGoals(false);
        }
      });
    } else {
      setIsGlobalEditMode(false);
      setHasNewGoals(false);
    }
  };

  const renderTask = (taskId: string) => {
    const task = tasks[taskId];
    const taskReviews = goalReviews.filter(review => review.goalAssignmentGoalId === task.goalAssignmentGoalId);
    const latestReview = taskReviews.length > 0
      ? taskReviews.sort((a, b) => new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime())[0]
      : null;

    const totalSubtasks = task.subtasks.length;
    const completedSubtasks = task.subtasks.filter(subtask => subtask.status === 'Completed').length;
    const inProgressSubtasks = task.subtasks.filter(subtask => subtask.status === 'In Progress').length;
    const completionPercentage = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

    const isAccepted = task.acceptanceStatus === 'Accepted';
    return (
      <div
        key={task.id}
        className={`task-card status-${task.status.toLowerCase().replace(' ', '-')}`}
        onClick={() => {
          if (isAccepted) {
            setSelectedTask(task.id);
            setIsModalOpen(true);
          }
        }}
        style={{ opacity: isAccepted ? 1 : 0.6, cursor: isAccepted ? 'pointer' : 'not-allowed' }}
      >
        <div className="task-header">
          <Typography type="heading5" text={task.title} />
          {/* Acceptance status and Accept button */}
          <div className="acceptance-status">
            {task.acceptanceStatus === 'Accepted' ? (
              <Typography type="small_regular" text="Accepted" className="accepted-status" />
            ) : task.acceptanceStatus === 'Declined' ? (
              <Typography type="small_regular" text="Declined" className="declined-status" />
            ) : (
              <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Button
                  type="primary"
                  size="sm"
                  onClick={() => handleAcceptGoal(task.id)}
                  className="accept-goal-btn"
                >
                  View & Accept / Decline
                </Button>
              </div>
            )}
          </div>
        </div>
        <Typography type="base_regular" text={task.description} />

        <div className="task-stats">
          <div className="completion-bar">
            <div
              className="completion-progress"
              style={{ width: `${completionPercentage}%` }}
            />
            <Typography
              type="small_medium"
              text={`${completionPercentage}%`}
              className="completion-text"
            />
          </div>

          <div className="stats-row">
            <div className="stat-item">
              <img src={ICONS.TASKS} alt="Total tasks" className="stat-icon" />
              <Typography type="small_medium" text={totalSubtasks.toString()} />
            </div>
            {completedSubtasks > 0 && (
              <div className="stat-item completed">
                <img src={ICONS.COMPLETED} alt="Completed tasks" className="stat-icon" />
                <Typography type="small_medium" text={completedSubtasks.toString()} />
              </div>
            )}
            {inProgressSubtasks > 0 && (
              <div className="stat-item in-progress">
                <img src={ICONS.IN_PROGRESS} alt="In progress tasks" className="stat-icon" />
                <Typography type="small_medium" text={inProgressSubtasks.toString()} />
              </div>
            )}
          </div>
        </div>

        {latestReview ? (
          <div className="goal-review-section">
            <div className="review-header">
              <Typography type="small_medium" text="Latest Review" className="review-title" />
              <div className="review-rating">
                {[...Array(5)].map((_, index) => (
                  <span
                    key={index}
                    className={`star ${index < latestReview.rating ? 'filled' : 'empty'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            <div className="review-content">
              <Typography type="small_regular" text={latestReview.reviewText} className="review-text" />
              <div className="review-meta">
                <Typography type="xs_regular" text={`By ${latestReview.reviewerName} (${latestReview.reviewerRole})`} className="reviewer-name" />
                <Typography type="xs_regular" text={new Date(latestReview.reviewDate).toLocaleDateString()} className="review-date" />
              </div>
            </div>
          </div>
        ) : null}

        <div className="task-footer">
          <div className="task-deadline">
            <Typography type="small_regular" text={`Assigned: ${moment(task.assignedDate).format("MMM D, YYYY")}`} />
            <Typography type="small_regular" text={`Deadline: ${moment(task.deadline).format("MMM D, YYYY")}`} />
          </div>
        </div>
      </div>
    );
  };

  const renderSubtask = (taskId: string, subtask: SubTaskWithQuarter) => {
    const isDragging = draggedSubtask?.subtaskId === subtask.id;
    const parentGoal = tasks[taskId];
    const isAccepted = parentGoal.acceptanceStatus === 'Accepted';
    const canDrag = isAccepted && isGlobalEditMode;

    return (
      <div
        key={subtask.id}
        draggable={canDrag}
        onDragStart={canDrag ? (e) => handleDragStart(e, taskId, subtask.id) : undefined}
        onDragEnd={handleDragEnd}
        onClick={() => isAccepted && handleSubtaskClick(taskId, subtask)}
        className={`subtask-item ${isDragging ? 'dragging' : ''} clickable`}
        style={{ opacity: isDragging ? 0.5 : isAccepted ? 1 : 0.6, cursor: isAccepted ? 'pointer' : 'not-allowed' }}
      >
        <div className={`status-dot status-${subtask.status.toLowerCase().replace(' ', '-')}`} />
        <div className="subtask-content">
          <div className="goal-indicator">
            <Typography type="small_regular" text={`Goal: ${parentGoal?.title || 'Unknown Goal'}`} className="goal-title" />
          </div>

          <div className="subtask-title">
            <Typography type="base_medium" text={subtask.title} className="title-text subtask-title-text" />
          </div>

          <div className="subtask-deadline">
            <Typography type="small_regular" text={`Due: ${moment(subtask.deadline).format("MMM D, YYYY")}`} className="deadline-text" />
          </div>

          <div className="subtask-priority">
            <span className={`priority-tag priority-${subtask.priority.toLowerCase()}`}>
              {subtask.priority}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderDroppableArea = (id: string, title: string) => {
    const isDraggedOver = draggedOver === id;
    const subtasksInQuarter = Object.entries(tasks).flatMap(([taskId, task]) =>
      task.subtasks
        .filter(subtask =>
          id === AVAILABLE_TASKS_ID
            ? !subtask.quarter
            : subtask.quarter === id
        )
        .map(subtask => ({ ...subtask, taskId }))
    ).filter(({ taskId }) => tasks[taskId].acceptanceStatus === 'Accepted'); // Only show for accepted goals

    const quarterInfo = id !== AVAILABLE_TASKS_ID ? quarterDateRanges[id as keyof typeof quarterDateRanges] : null;

    return (
      <div
        className={`droppable-container ${isDraggedOver ? 'dragging-over' : ''}`}
        onDragOver={isGlobalEditMode ? handleDragOver : undefined}
        onDragEnter={isGlobalEditMode ? (e) => handleDragEnter(e, id) : undefined}
        onDragLeave={isGlobalEditMode ? handleDragLeave : undefined}
        onDrop={isGlobalEditMode ? (e) => handleDrop(e, id) : undefined}
      >
        <div className="column-title">
          <Typography type="heading5" text={title} />
          {quarterInfo && (
            <Typography
              type="small_regular"
              text={`${moment(quarterInfo.start).format("MMM D, YYYY")} - ${moment(quarterInfo.end).format("MMM D, YYYY")}`}
              className="quarter-dates"
            />
          )}
        </div>
        <div className="subtask-list">
          {subtasksInQuarter.map(subtask => renderSubtask(subtask.taskId, subtask))}
          {subtasksInQuarter.length === 0 && (
            <div className="empty-quarter">
              <Typography
                type="small_regular"
                text={id === AVAILABLE_TASKS_ID ? "No unassigned subtasks" : "Drop subtasks here"}
                className="empty-text"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const headerItem1 = (
    <div className="breadcrumb-container">
      <NewBreadCrumb />
    </div>
  );

  const mainItem2 = (
    <div className="my-goals-container">
      {loading || employeeIdLoading ? (
        <div className="loading-container">
          <LogoSVG />
          <Typography type="base_medium" text="Loading goals, Please Wait..." className="loading-text" />
        </div>
      ) : (!goalAssignments || goalAssignments.length === 0 || Object.keys(tasks).length === 0) ? (
        <div className="no-goals-message">
          <img src="/icons/dashboard.svg" alt="No Goals" className="no-goals-icon enhanced" />
          <Typography
            type="heading5"
            text="You don't have any assigned goals yet."
            className="no-goals-title"
          />
          <Typography
            type="base_regular"
            text="When you have goals to achieve, they will appear here."
            className="no-goals-desc"
          />
        </div>
      ) : (
        <>
          <div className="my-goals-header">
            <div className="supervisor-info">
              <div className="info-sections-container">
                <div className="supervisor-details">
                  <div className="supervisor-header">
                    <Typography type="heading5" text="Your Supervisors Information" />
                  </div>
                  <div className="supervisor-content">
                    {supervisors && supervisors.length > 0 ? (
                      supervisors.map((supervisor, index) => (
                        <div key={supervisor.id || index} className="supervisor-item">
                          <div className="supervisor-info-group">
                            <div className="info-row">
                              <Typography type="base_regular" text="Name:" className="label" />
                              <Typography type="base_medium" text={supervisor?.name || "N/A"} />
                            </div>
                            <div className="info-row">
                              <Typography type="base_regular" text="Title:" className="label" />
                              <Typography type="base_regular" text={supervisor?.designation || "N/A"} />
                            </div>
                            <div className="info-row">
                              <Typography type="base_regular" text="Email:" className="label" />
                              <Typography type="base_regular" text={supervisor?.email || "N/A"} className="email" />
                            </div>
                          </div>
                          {index < supervisors.length - 1 && <div className="supervisor-separator"></div>}
                        </div>
                      ))
                    ) : (
                      <div className="info-row">
                        <Typography type="base_regular" text="No supervisors assigned" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* header right container for logo  */}
            <div className="header-right-container">
              <img src="/icons/aventude.svg" alt="Logo" className="aventude-logo" />
            </div>


          </div>
          <div className="header-save-edit">
            <div className="quarter-management-info">
              <Typography
                type="small_regular"
                text="Use Save/Edit buttons on goals to manage subtasks. Drag subtasks when in edit mode to organize into quarters."
                className="quarter-info"
              />

              {/* Global Edit/Save Controls */}
              <div className="global-actions">
                {hasNewGoals && (
                  <div className="new-goals-notification">
                    <Typography type="small_regular" text="🎯 New goals assigned! Please organize subtasks by quarters." className="new-goals-text" />
                  </div>
                )}

                {pendingChanges && (
                  <div className="pending-changes-indicator">
                    <Typography type="small_regular" text="● Unsaved changes Please Save Changes before leaving the page" className="pending-text" />
                  </div>
                )}

                <div className="action-buttons">
                  {!isGlobalEditMode ? (
                    <button
                      className="edit-btn global-edit"
                      onClick={handleEnterEditMode}
                      disabled={Object.keys(tasks).length === 0}
                    >
                      <img src="/icons/edit.svg" alt="Edit" />
                      <Typography type="small_medium" text="Edit Subtasks" />
                    </button>
                  ) : (
                    <>
                      <button
                        className="cancel-btn"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                      >
                        <Typography type="small_medium" text="Cancel" />
                      </button>
                      <button
                        className="save-btn global-save"
                        onClick={handleGlobalSave}
                        disabled={isSaving || !pendingChanges}
                      >
                        {isSaving ? (
                          <>
                            <div className="spinner"></div>
                            <Typography type="small_medium" text="Saving..." />
                          </>
                        ) : (
                          <>
                            <img src="/icons/send.svg" alt="Save" />
                            <Typography type="small_medium" text="Save All Changes" />
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="goals-section">
            <div className="goals-list">
              <div className="goals-header">
                <Typography type="heading5" text="Goals" />
                <Typography type="small_regular" text="Click on a goal to view and manage subtasks" />
              </div>
              {Object.keys(tasks).map(taskId => renderTask(taskId))}
            </div>

            <div className="quarters-section">
              <div className="quarters-nav-bar">
                <div className="nav-bar-content">
                  <Typography type="heading5" text="Quarterly Planning" className="nav-title" />
                  <div className="nav-actions">
                    <Typography type="small_regular" text="Drag subtasks to organize them into quarters" className="nav-instruction" />
                    <div className="quarter-stats">
                      {quarters.map(quarter => {
                        const quarterSubtasks = Object.values(tasks).flatMap(task =>
                          task.subtasks.filter(subtask => subtask.quarter === quarter)
                        );
                        return (
                          <div key={quarter} className="quarter-stat">
                            <Typography type="small_medium" text={quarter} />
                            <Typography type="small_regular" text={`${quarterSubtasks.length} tasks`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="quarters-content">
                <div className="available-column">
                  {renderDroppableArea(AVAILABLE_TASKS_ID, 'Available Subtasks')}
                </div>
                <div className="quarters-container">
                  {quarters.map((quarter) => (
                    <div key={quarter} className="quarter-column">
                      {renderDroppableArea(quarter, quarter)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (loading || employeeIdLoading) {
    return (
      <div className="loading-container">
        <LogoSVG />
      </div>
    );
  }

  if (error) {
    return <Typography type="base_regular" text="Failed to load goals. Please try again." className="error-message" />;
  }

  return (
    <>
      <ViewScreen
        portalTitle="My Goals"
        headerItem1={headerItem1}
        mainItem2={mainItem2}
      />
      <TaskDetailsModal
        task={selectedTask ? tasks[selectedTask] : null}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTask(null);
        }}
        onAddSubtask={(taskId, subtask) => {
          const newSubtask = {
            ...subtask,
            id: `subtask-${Date.now()}`
          };
          setTasks(prev => ({
            ...prev,
            [taskId]: {
              ...prev[taskId],
              subtasks: [...prev[taskId].subtasks, newSubtask]
            }
          }));
        }}
        onStatusChange={handleStatusChange}
        onSubtaskStatusChange={handleSubtaskStatusChange}
        onSubtaskDelete={handleSubtaskDelete}
        onSubtaskEdit={handleSubtaskEdit}
        isSaved={!isGlobalEditMode}
        goalReviews={selectedTask ? goalReviews.filter(review => review.goalAssignmentGoalId === tasks[selectedTask]?.goalAssignmentGoalId) : []}
      />

      <SubtaskDetailsModal
        isOpen={isSubtaskModalOpen}
        onClose={() => {
          setIsSubtaskModalOpen(false);
          setSelectedSubtask(null);
        }}
        subtask={selectedSubtask?.subtask || null}
        goalTitle={selectedSubtask ? tasks[selectedSubtask.taskId]?.title || 'Unknown Goal' : ''}
        taskId={selectedSubtask?.taskId || ''}
        isSaved={!isGlobalEditMode}
        onStatusChange={handleSubtaskStatusChange}
        onDeadlineChange={handleSubtaskDeadlineChange}
        onSubtaskUpdate={handleSubtaskUpdate}
        onSubtaskDelete={handleSubtaskDelete}
        quarterRange={selectedSubtask?.subtask?.quarter ? quarterDateRanges[selectedSubtask.subtask.quarter] : null}
      />

      <GoalDetailsModal
        isOpen={isGoalDetailsModalOpen}
        onClose={() => {
          setIsGoalDetailsModalOpen(false);
          setSelectedGoalForAcceptance(null);
        }}
        goal={selectedGoalForAcceptance ? tasks[selectedGoalForAcceptance] : null}
        onAccept={handleGoalAccept}
        onDecline={handleGoalDecline}
        isProcessing={isProcessingGoal}
      />
    </>
  );
};